import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorBoundary } from '@sentry/react';
import {
  PatchscriptPreview,
  PatchscriptTemplate,
  PatchscriptTemplateEditor,
} from '@pos-refacto/patchscript-with-react';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import {
  Box,
  Button,
  ButtonGroup,
  Fab,
  ThemeProvider,
  Typography,
} from '@material-ui/core';
import {
  MdContentCopy,
  MdContentPaste,
  MdEdit,
  MdReceipt,
} from 'react-icons/all';

import {
  deleteCAFAConfig,
  saveCafaConfig,
} from 'actions/integrations/CafaConfigs';
import { INTEGRATION_TYPES } from 'constants/CAFA';
import {
  getDefaultTemplateForType,
  getTemplateForType,
  hasTemplateForType,
  sampleDataForType,
} from 'reducers/patchScript';
import Loader from 'components/Loader';
import UIButton from 'components/UIElements/UIButton';
import { composeReceiptWithSchema } from 'reducers/patchScript/composeReceiptWithSchema';
import { getClientCode, getSessionKey } from 'reducers/Login';
import { clearCachedTemplate } from 'actions/integrations/printer/receiptPrintoutApi/api';
import CloseButton from 'components/CustomButtons/CloseButton';
import MuiButton from 'components/CustomButtons/MuiButton';
import { useBreakpoints } from 'utils/hooks/UI';
import { lightTheme } from 'utils/muiTheme';

import styles from './ReceiptSchema/styles.module.scss';

function FullScreenLayout({ editor, preview, onClose, controls }) {
  const breakpoints = useBreakpoints();
  const [view, setView] = useState<'edit' | 'preview'>('edit');
  return (
    <ThemeProvider theme={lightTheme}>
      <div
        className={`${styles.reset} ${styles.fullScreenEditor}`}
        data-mobile={!breakpoints.md}
      >
        <div className={styles.header}>
          <Box flexShrink={1} overflow="hidden">
            {controls}
          </Box>
          <Box flexGrow={1} />
          <CloseButton action={onClose} />
        </div>
        <div data-selected={view === 'preview'} className={styles.preview}>
          {preview}
        </div>
        <div data-selected={view === 'edit'} className={styles.editor}>
          {editor}
        </div>
        <div className={styles.fab}>
          {view === 'preview' ? (
            <Fab color="primary" onClick={() => setView('edit')} size="small">
              <MdEdit size={24} />
            </Fab>
          ) : (
            <Fab color="secondary" onClick={() => setView('preview')} size="small">
              <MdReceipt size={24} />
            </Fab>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
}

export const ReceiptSchema = ({
  receiptType = 'salesReceipt',
}: {
  receiptType: Parameters<typeof sampleDataForType>[0];
}) => {
  const dispatch: ThunkDispatch<unknown, unknown, Action> = useDispatch();

  /* Sample data - last receipt */
  const [sampleData, setSampleData] = useState<any>({});
  const [editorOpen, setEditorOpen] = useState(false);
  useEffect(() => {
    dispatch(sampleDataForType(receiptType)).then(setSampleData);
  }, [dispatch, receiptType]);
  /* Template - editable by the user but resets if changed externally */
  const [template, setTemplate] = useState<PatchscriptTemplate>([]);
  const hasTemplate = useSelector(hasTemplateForType(receiptType));
  const currentTemplate = useSelector(getTemplateForType(receiptType));
  const defaultTemplate = useSelector(getDefaultTemplateForType(receiptType));
  useEffect(() => setTemplate(currentTemplate), [currentTemplate]);

  const sessionKey = useSelector(getSessionKey);
  const clientCode = useSelector(getClientCode);

  const persist = () => {
    if (template === undefined) {
      dispatch(
        deleteCAFAConfig({
          integrationName: receiptType,
          integrationType: INTEGRATION_TYPES.patchScriptTemplate,
        }),
      );
    } else {
      dispatch(
        saveCafaConfig({
          integrationName: receiptType,
          integrationType: INTEGRATION_TYPES.patchScriptTemplate,
          config: template,
        }),
      );
    }
    clearCachedTemplate(sessionKey, clientCode);
  };

  const controls = (
    <Controls
      currentTemplate={currentTemplate}
      template={template}
      setTemplate={setTemplate}
      commit={persist}
    />
  );
  const editor = (
    <ErrorBoundary
      fallback={({ error, resetError }) => (
        <>
          <h1>An error has occured in the patchscript editor</h1>
          <p>{error?.message}</p>
          <button type="button" onClick={resetError}>
            Retry
          </button>
          <button type="button" onClick={() => setEditorOpen(false)}>
            Close
          </button>
        </>
      )}
    >
      <PatchscriptTemplateEditor
        slice={template ?? defaultTemplate}
        onChange={setTemplate}
        scope={sampleData}
      />
    </ErrorBoundary>
  );
  const preview = (
    <ErrorBoundary
      fallback={({ error, resetError }) => (
        <>
          <h1>An error has occured trying to preview the receipt</h1>
          <p>{error?.message}</p>
          <button type="button" onClick={resetError}>
            Retry
          </button>
        </>
      )}
    >
      <div style={{ width: '48ch' }}>
        <PatchscriptPreview
          receiptData={composeReceiptWithSchema(
            template ?? defaultTemplate,
            sampleData,
          )}
        />
      </div>
    </ErrorBoundary>
  );
  if (editorOpen) {
    return ReactDOM.createPortal(
      <FullScreenLayout
        preview={preview}
        editor={editor}
        onClose={() => setEditorOpen(false)}
        controls={controls}
      />,
      document.body,
    );
  }
  return (
    <>
      <MuiButton color="primary" onClick={() => setEditorOpen(true)}>
        Edit receipt template
      </MuiButton>
      {currentTemplate !== template && (
        <MuiButton variant="contained" color="grid_green" onClick={persist}>
          Save changes
        </MuiButton>
      )}
      <div
        style={{
          color: 'black',
          backgroundColor: 'white',
          padding: '1ch 2ch 1ch 1ch',
        }}
      >
        <Loader
          loadingText="Loading last invoice"
          show={Object.keys(sampleData).length === 0}
        >
          {preview}
        </Loader>
      </div>
    </>
  );
};

function Controls({
  currentTemplate,
  template,
  setTemplate,
  commit,
}: {
  /** Currently saved template */
  currentTemplate: PatchscriptTemplate;
  /** Unsaved / edited version of the template */
  template: PatchscriptTemplate;
  /** False if we are rendering the default template */
  setTemplate: (t: PatchscriptTemplate) => void;
  commit: () => void;
}) {
  const loadFromClipboard = () =>
    navigator.clipboard
      .readText()
      .then(JSON.parse)
      .then(setTemplate);
  const saveToClipboard = () =>
    navigator.clipboard.writeText(JSON.stringify(template));
  return (
    <>
      <div>
        <ButtonGroup>
          <Button startIcon={<MdContentCopy />} onClick={saveToClipboard}>
            Copy
          </Button>
          <Button endIcon={<MdContentPaste />} onClick={loadFromClipboard}>
            Paste
          </Button>
        </ButtonGroup>
      </div>
      <div>
        {currentTemplate !== template ? (
          <ButtonGroup>
            <MuiButton
              variant="contained"
              color="grid_red"
              onClick={() => setTemplate(currentTemplate)}
            >
              Discard changes
            </MuiButton>
            <MuiButton variant="contained" color="grid_green" onClick={commit}>
              Save changes
            </MuiButton>
          </ButtonGroup>
        ) : (
          <Button disabled variant="contained">
            No changes / All changes saved
          </Button>
        )}
      </div>
      <div>
        {template === undefined ? null : (
          <UIButton
            text="Revert to default template"
            variant="danger"
            action={() => setTemplate(undefined)}
          />
        )}
      </div>
    </>
  );
}
